import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { AUTH, environment } from '@vaki/shared/util/environment';
import { ErrorTrackingRuntimeConfigService } from './error-tracking-runtime-config.service';

/**
 * Runs once, after Angular's injector exists (wired as a
 * `provideAppInitializer` in app.config.browser.ts — see that file). Two
 * jobs `sentry-browser.init.ts` can't do at its pre-bootstrap, DI-free
 * call site:
 *
 *  1. AC8/AC2 — starts `ErrorTrackingRuntimeConfigService` mirroring
 *     Remote Config into `errorTrackingRuntimeState`, which `beforeSend`
 *     (registered back in `sentry-browser.init.ts`) reads on every event.
 *  2. AC1 ("... y user id") — tags events with Vaki's internal uid, never
 *     email/PII (`scrubSentryEvent` would strip it anyway if a call site
 *     ever set more — this only ever sets `id`).
 */
export function wireErrorTrackingRuntime(): void {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) return;
  if (!environment.sentry.enabled || !environment.sentry.dsn) return;

  inject(ErrorTrackingRuntimeConfigService).startSyncing();

  const auth = inject(AUTH) as Auth;
  onAuthStateChanged(auth, (user) => {
    Sentry.setUser(user ? { id: user.uid } : null);
  });
}
